'use client';

import { useIsomorphicEffect } from '@ocodelib/mui/hooks';
import { createContext, useMemo, useRef } from 'react';
import { P5BasicAppManager } from './P5BasicAppManager';
import { useP5BasicAppLoader } from './app';
import type { IAppConfig, IP5BasicLayoutConfig, P5BasicAppType } from './types';

export interface IP5BasicAppContext {
  appManager: P5BasicAppManager;
  app: P5BasicAppType;
}

export const P5BasicAppContext = createContext<IP5BasicAppContext | null>(null);

interface Props {
  layoutConfig: IP5BasicLayoutConfig;
  appConfig: IAppConfig;
  children: React.ReactNode;
  onLoadApp?: (app: P5BasicAppType | null) => void;
}

export function P5BasicAppProvider(props: Props) {
  const { layoutConfig, children } = props;
  const appConfigRef = useRef(props.appConfig);
  appConfigRef.current = props.appConfig;

  const appManager = useMemo(() => new P5BasicAppManager(appConfigRef.current), []);
  const { app } = useP5BasicAppLoader({
    previewMode: props.appConfig.previewMode ?? false,
    newProjectLoader: props.appConfig.newProjectLoader,
    initialProjectLoader: props.appConfig.initialProjectLoader,
  });

  useIsomorphicEffect(() => {
    appManager.setup(layoutConfig ?? null);
  }, [appManager, layoutConfig]);

  useIsomorphicEffect(() => {
    appManager.setApp(app ?? null);
  }, [appManager, app]);

  const contextData: IP5BasicAppContext | null = useMemo(
    () =>
      app
        ? {
            appManager,
            app,
          }
        : null,
    [appManager, app],
  );

  if (!contextData) return null;
  return <P5BasicAppContext.Provider value={contextData}>{children}</P5BasicAppContext.Provider>;
}
